import type { FileQueryParams, IFileRepository, PaginatedResponse } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { FileCategory, FileVisibility, PublicLinkResponse, UploadedFile } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiFileResponse {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  category: string;
  url: string;
  uploadedBy: string;
  createdAt: string;
  isReferenced: boolean;
  visibility?: string;
  sharedWithUsers?: string[];
  sharedWithRoles?: string[];
  publicToken?: string;
}

interface ApiDownloadResponse {
  url: string;
  expiresAt: string;
}

// Mapear respuesta de API a tipo interno
const mapApiFileToUploadedFile = (apiFile: ApiFileResponse): UploadedFile => ({
  id: apiFile.id,
  url: apiFile.url,
  filename: apiFile.filename,
  originalName: apiFile.originalName,
  mimeType: apiFile.mimeType,
  size: apiFile.size,
  category: apiFile.category as FileCategory,
  uploadedBy: apiFile.uploadedBy,
  uploadedAt: apiFile.createdAt,
  isReferenced: apiFile.isReferenced,
  visibility: apiFile.visibility as FileVisibility | undefined,
  sharedWithUsers: apiFile.sharedWithUsers,
  sharedWithRoles: apiFile.sharedWithRoles,
  publicToken: apiFile.publicToken,
});

export class ApiFileRepository implements IFileRepository {
  async uploadFile(file: File, category: FileCategory, customName?: string): Promise<UploadedFile> {
    const client = getApiClient();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (customName) {
      formData.append('customName', customName);
    }

    const apiFile = await client.post<ApiFileResponse>('v1/files/upload', formData);
    return mapApiFileToUploadedFile(apiFile);
  }

  async uploadFileFromUri(
    uri: string,
    filename: string,
    mimeType: string,
    category: FileCategory
  ): Promise<UploadedFile> {
    const client = getApiClient();
    // React Native FormData format
    const formData = new FormData();
    formData.append('file', {
      uri,
      name: filename,
      type: mimeType,
    } as unknown as Blob);
    formData.append('category', category);

    const apiFile = await client.post<ApiFileResponse>('v1/files/upload', formData);
    return mapApiFileToUploadedFile(apiFile);
  }

  async getFiles(params?: FileQueryParams): Promise<PaginatedResponse<UploadedFile>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      category: params?.category,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    });
    const response = await client.get<PaginatedResponse<ApiFileResponse>>(
      `v1/files?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiFileToUploadedFile),
    };
  }

  async getFileById(id: string): Promise<UploadedFile | null> {
    const client = getApiClient();
    try {
      const apiFile = await client.get<ApiFileResponse>(`v1/files/${id}`);
      return mapApiFileToUploadedFile(apiFile);
    } catch {
      return null;
    }
  }

  async deleteFile(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/files/${id}`);
  }

  async getDownloadUrl(id: string): Promise<string> {
    const client = getApiClient();
    const response = await client.get<ApiDownloadResponse>(`v1/files/${id}/download`);
    return response.url;
  }

  async markAsReferenced(id: string): Promise<void> {
    const client = getApiClient();
    await client.put(`v1/files/${id}/reference`);
  }

  // --- Sharing Methods ---

  async updateVisibility(id: string, visibility: FileVisibility): Promise<UploadedFile> {
    const client = getApiClient();
    const apiFile = await client.put<ApiFileResponse>(`v1/files/${id}/visibility`, { visibility });
    return mapApiFileToUploadedFile(apiFile);
  }

  async shareWithUsers(id: string, userIds: string[]): Promise<UploadedFile> {
    const client = getApiClient();
    const apiFile = await client.post<ApiFileResponse>(`v1/files/${id}/share/users`, { userIds });
    return mapApiFileToUploadedFile(apiFile);
  }

  async unshareWithUser(id: string, userId: string): Promise<UploadedFile> {
    const client = getApiClient();
    const apiFile = await client.delete<ApiFileResponse>(`v1/files/${id}/share/users/${userId}`);
    return mapApiFileToUploadedFile(apiFile);
  }

  async shareWithRoles(id: string, roles: string[]): Promise<UploadedFile> {
    const client = getApiClient();
    const apiFile = await client.put<ApiFileResponse>(`v1/files/${id}/share/roles`, { roles });
    return mapApiFileToUploadedFile(apiFile);
  }

  async generatePublicLink(id: string): Promise<PublicLinkResponse> {
    const client = getApiClient();
    return client.post<PublicLinkResponse>(`v1/files/${id}/public-link`);
  }

  async revokePublicLink(id: string): Promise<UploadedFile> {
    const client = getApiClient();
    const apiFile = await client.delete<ApiFileResponse>(`v1/files/${id}/public-link`);
    return mapApiFileToUploadedFile(apiFile);
  }
}
